import type { PlayerSettings } from './player/settings';
import type { IconName } from './icons';

export type SubscriptionStatus = 'active' | 'inactive' | 'none';

export type ProductAccessScope = 'all' | 'selected';

/** A mini-course product the user owns, shown in profile «Мої продукти». */
export interface SessionProductDTO {
  id: string;
  title: string;
  accessScope: ProductAccessScope;
  status: SubscriptionStatus;
  endDate: string | null;
}

export interface SessionDTO {
  telegramId: string;
  name: string;
  lastName: string | null;
  fullName: string;
  username: string | null;
  photo: string | null;
  status: SubscriptionStatus;
  subscriptionEndDate: string | null;
  tariff: string | null;
  subscriptionStartDate: string | null;
  isAdmin: boolean;
  /** Owned mini-course products (Club excluded — see subscription fields). */
  products: SessionProductDTO[];
  /** Bot username for the current environment — used to build deep links. */
  botUsername: string;
  /** Whether this environment may write to Bunny. False on dev/local (read-only
   *  mirror) → the admin UI hides material/section creation, upload and delete. */
  bunnyWritable: boolean;
  /** Чи показувати реферальну систему. На проді вимкнено; це лише видимість. */
  referralsVisible: boolean;
  /** Session JWT — replayed via the Authorization header where cookies don't
   *  stick (Telegram Android WebView). Stored client-side after login. */
  token?: string;
}

export interface CategoryDTO {
  id: string;
  icon: IconName;
  name: string;
  description: string | null;
  sortOrder: number;
  totalMaterials: number;
  /** Whether the user can open this group (drives dimming + upsell). */
  hasAccess: boolean;
  /** No full access, but at least one lesson is individually unlocked — the
   *  group floats up and shows no lock. */
  hasPartialAccess: boolean;
}

export type MaterialType = 'video' | 'audio' | 'text';

export interface MaterialSummaryDTO {
  id: string;
  type: MaterialType;
  title: string;
  description: string | null;
  durationSec: number | null;
  thumbnailUrl: string | null;
  tags: string[];
  isFavorite: boolean;
  maxProgressPct: number;
  completedAt: string | null;
  /** Whether the user can open THIS lesson (category fully unlocked or the
   *  lesson individually granted). Drives the per-lesson lock overlay. */
  hasAccess: boolean;
}

export interface CategoryDetailDTO {
  id: string;
  icon: IconName;
  name: string;
  description: string | null;
  sortOrder: number;
  /** Whether the user can open this group's materials. */
  hasAccess: boolean;
  materials: MaterialSummaryDTO[];
}

export interface PlaybackStateDTO {
  lastPosition: number;
  durationSec: number;
  maxProgressPct: number;
  completedAt: string | null;
}

export interface RecentItemDTO {
  id: string;
  type: MaterialType;
  title: string;
  durationSec: number | null;
  thumbnailUrl: string | null;
  category: { id: string; icon: IconName; name: string };
  maxProgressPct: number;
  completedAt: string | null;
  lastOpenedAt: string;
}

export interface FavoriteItemDTO {
  id: string;
  type: MaterialType;
  title: string;
  description: string | null;
  durationSec: number | null;
  thumbnailUrl: string | null;
  category: { id: string; icon: IconName; name: string };
  favoritedAt: string;
  maxProgressPct: number;
  completedAt: string | null;
}

export interface ProgressByCategoryItemDTO {
  id: string;
  icon: IconName;
  name: string;
  completed: number;
  total: number;
  pct: number;
}

export interface AdminUserDTO {
  telegramId: string;
  name: string;
  lastName: string | null;
  fullName: string;
  username: string | null;
  photoUrl: string | null;
  email: string | null;
  phone: string | null;
  /** All known emails/phones (detail view only). Includes the primary above. */
  emails?: string[];
  phones?: string[];
  status: SubscriptionStatus;
  subscriptionEndDate: string | null;
  tariff: string | null;
  subscriptionStartDate: string | null;
  isAdmin: boolean;
  createdAt: string;
  /** First-touch: з якого креативу контакт до нас потрапив. Фіксується один раз;
   *  `firstTouchAt = null` = атрибуції немає (контакт з'явився до впровадження
   *  UTM або мітки не приходили). */
  firstUtmSource: string | null;
  firstUtmMedium: string | null;
  firstUtmCampaign: string | null;
  firstUtmContent: string | null;
  firstUtmTerm: string | null;
  firstTouchAt: string | null;
  /** 'sendpulse' | 'payment' — звідки взялась атрибуція. */
  firstTouchVia: string | null;
  /** All payments linked to this contact (detail view only), newest first. */
  payments?: AdminUserPaymentDTO[];
}

export interface AdminUserPaymentDTO {
  id: string;
  occurredAt: string;
  status: PaymentStatus;
  amountCents: number;
  currency: string;
  product: { id: string; title: string } | null;
  rawProduct: string | null;
}

export interface AdminUsersResponse {
  items: AdminUserDTO[];
  total: number;
  page: number;
  hasMore: boolean;
}

/** Block-based content used inside materials (replaces cover/text/mediaGroup). */
export type TextBlock = { id: string; type: 'text'; markdown: string };
export type ImageWidth = 'full' | 'medium' | 'small';
export type ImageBlock = {
  id: string;
  type: 'image';
  url: string;
  /** Display width of the image relative to the column. Undefined = 'full'
   *  (rendered edge-to-edge); smaller sizes center horizontally. */
  width?: ImageWidth;
};
export type GalleryColumns = 2 | 3 | 4;
export type GalleryBlock = { id: string; type: 'gallery'; urls: string[]; cols?: GalleryColumns };
export type Block = TextBlock | ImageBlock | GalleryBlock;

export interface AdminMaterialDTO {
  id: string;
  type: MaterialType;
  title: string;
  description: string | null;
  textContent: string | null;
  durationSec: number | null;
  category: { id: string; icon: IconName; name: string } | null;
  tags: string[];
  isPublished: boolean;
  sortOrder: number;
  publishedAt: string | null;
  createdAt: string;
  bunnyMissingAt: string | null;
  body: Block[];
  chapters: MaterialChapter[];
}

export interface AdminMaterialsResponse {
  items: AdminMaterialDTO[];
  total: number;
  page: number;
  hasMore: boolean;
}

export interface AdminCategoryDTO {
  id: string;
  icon: IconName;
  name: string;
  isActive: boolean;
  totalMaterials: number;
}

export interface AdminTariffDTO {
  id: string;
  title: string;
  /** Payment names that resolve to this tariff. */
  paymentKeys: string[];
  /** Access period in days: >0 = subscription, null = lifetime. */
  accessDays: number | null;
  /**
   * Чи створює цей тариф точки продовження у статистиці утримання. Для
   * пробного доступу вимикають: його закінчення — конверсія в платний тариф,
   * а не втрата підписки. На доступ не впливає.
   */
  countsInRenewal: boolean;
  sortOrder: number;
}

export interface AdminProductDTO {
  id: string;
  title: string;
  description: string | null;
  accessScope: ProductAccessScope;
  sendpulseVar: string | null;
  /** Legacy product-level payment names (fallback when no tariff matches). */
  paymentKeys: string[];
  isSystem: boolean;
  sortOrder: number;
  /** Category ids this product unlocks in full (empty for accessScope='all'). */
  categoryIds: string[];
  categoriesCount: number;
  /** Individually-granted lesson ids from categories not fully unlocked. */
  materialIds: string[];
  tariffs: AdminTariffDTO[];
}

/** Create/patch payload for a product (incl. its tariffs). */
export interface AdminProductBody {
  title: string;
  description?: string | null;
  accessScope: 'all' | 'selected';
  sendpulseVar?: string | null;
  paymentKeys?: string[];
  categoryIds?: string[];
  materialIds?: string[];
  tariffs?: Array<{
    id?: string;
    title: string;
    paymentKeys: string[];
    accessDays: number | null;
    countsInRenewal: boolean;
  }>;
}

// ── Розклад / events ──

export interface AdminEventDTO {
  id: string;
  title: string;
  description: string | null;
  startsAt: string; // ISO
  durationMin: number | null;
  streamUrl: string | null;
  bunnyVideoId: string | null;
  recordingSec: number | null;
  hasRecording: boolean;
  productIds: string[];
  createdAt: string;
}

/** Create/patch payload for an event. */
export interface AdminEventBody {
  title: string;
  description?: string | null;
  startsAt: string; // ISO
  durationMin?: number | null;
  streamUrl?: string | null;
  bunnyVideoId?: string | null;
  recordingSec?: number | null;
  productIds?: string[];
}

/** A participant in the event stats drill-in (clicked «Приєднатись»). */
export interface EventParticipant {
  telegramId: string;
  fullName: string;
  username: string | null;
  photoUrl: string | null;
  email: string | null;
  phone: string | null;
  status: SubscriptionStatus;
  clicksCount: number;
}

export interface EventStatsDTO {
  totalClicks: number;
  uniqueParticipants: number;
  recordingViewers: number;
  participants: EventParticipant[];
  total: number;
  page: number;
  hasMore: boolean;
}

/** User-facing event row (home / schedule lists). */
export interface EventListItemDTO {
  id: string;
  title: string;
  description: string | null;
  startsAt: string;
  durationMin: number | null;
  hasAccess: boolean;
  locked: boolean;
  hasRecording: boolean;
}

/** User-facing event detail. streamUrl present only when hasAccess. */
export interface EventDetailDTO extends EventListItemDTO {
  streamUrl: string | null;
}

/** Recording resume state (mirror of material playback state). */
export interface EventRecordingProgress {
  lastPosition: number;
  durationSec: number;
  maxProgressPct: number;
  completedAt: string | null;
}

export type DashboardPeriod =
  | 'dayPick'
  | 'week'
  | 'month'
  | 'all'
  | 'custom'
  | 'monthPick'
  | 'yearPick';
export type PaymentStatus = 'succeeded' | 'refunded' | 'failed' | 'pending';

export interface AdminDashboardStatsDTO {
  period: DashboardPeriod;
  activeMembers: number;
  /**
   * Продовження за період — подієво: рахуються точки, де доступ мав
   * закінчитись, а не факт оплати. Дострокове продовження зараховується в той
   * період, за який воно утримало учасницю.
   */
  renewals: {
    /** false для продуктів без підписки (міні-курси) — показуємо «—». */
    applicable: boolean;
    /** Мали продовжити = renewedCount + lapsedCount + pendingCount. */
    dueCount: number;
    renewedCount: number;
    lapsedCount: number;
    /** Строк вийшов щойно, ще в межах grace — рішення попереду. */
    pendingCount: number;
    /** renewed / (renewed + lapsed), %. */
    ratePct: number;
    /**
     * Сума платежів, які закрили ці точки продовження. Свідомо НЕ складається
     * з `payments.grossCents`: дострокова оплата надійшла в касу попереднього
     * місяця, але втримала цей. Гроші прив'язані до когорти, щоб описувати тих
     * самих людей, що й `renewedCount`.
     */
    renewedRevenueCents: number;
    /** Середній чек продовження. */
    renewedAvgCents: number;
  };
  /**
   * @deprecated Дублює `renewals.lapsedCount`. Лишається в API заради
   * кешованих збірок у Telegram WebView; новий код читає `renewals`.
   */
  endedNotRenewed?: number;
  /**
   * Ті самі показники за попередній період такої ж довжини — база для дельт на
   * плитках. null для періоду «за весь час»: порівнювати нема з чим.
   */
  prev: {
    /** Межі попереднього вікна (ISO) — для підпису «проти 1–31 травня». */
    from: string;
    to: string;
    activeMembers: number;
    newCount: number;
    renewedCount: number;
    lapsedCount: number;
    grossCents: number;
    count: number;
    avgCents: number;
    refundCount: number;
  } | null;
  viewsInPeriod: number;
  payments: {
    grossCents: number;
    refundCents: number;
    netCents: number;
    count: number;
    avgCents: number;
    refundCount: number;
    newCount: number;
    /**
     * @deprecated Повторні оплати (money-side). Лишається заради кешованих
     * збірок; метрика продовжень — у `renewals`.
     */
    renewedCount?: number;
  };
  revenueByProduct: Array<{
    productId: string | null;
    title: string;
    netCents: number;
    count: number;
    /** Per-tariff breakdown (empty for «Не зіставлено»). */
    tariffs: Array<{ tariffId: string | null; title: string; netCents: number; count: number }>;
    /** Розбивка по маркетингових джерелах (воронках). */
    sources: Array<{ source: string | null; title: string; netCents: number; count: number }>;
    /** Розбивка по utm_source (креатив) — окрема вісь від `sources` (воронка).
     *  Опційне: старі збірки API його не віддають. */
    utmSources?: Array<{ source: string | null; title: string; netCents: number; count: number }>;
  }>;
  activityByDay: Array<{ date: string; weekday: string; count: number }>;
  /** Top-5 most-liked materials (distinct likers, all-time). */
  popularMaterials: PopularMaterialDTO[];
}

/** A liked material with its like count (dashboard «Популярні матеріали»). */
export interface PopularMaterialDTO {
  id: string;
  type: MaterialType;
  title: string;
  category: { id: string; icon: IconName; name: string } | null;
  likesCount: number;
}

export interface PopularMaterialsResponse {
  items: PopularMaterialDTO[];
  total: number;
  page: number;
  hasMore: boolean;
}

/** A person active on a given day (drill-in behind an «Активність по днях» bar). */
export interface ActivityMember {
  telegramId: string;
  fullName: string;
  username: string | null;
  photoUrl: string | null;
  email: string | null;
  phone: string | null;
  status: SubscriptionStatus;
  /** How many materials they last-opened that day. */
  materialsCount: number;
}

export interface ActivityMembersResponse {
  items: ActivityMember[];
  total: number;
  page: number;
  hasMore: boolean;
}

/** Month analytics for the «Активність» page (event-log based). */
export interface ActivityStatsDTO {
  month: string; // YYYY-MM
  /** One entry per calendar day of the month; count = distinct active users. */
  heatmap: Array<{ date: string; count: number }>;
  /** dow 0..6 (Sun..Sat); avg active users + avg views (opens) on that weekday. */
  weekday: Array<{ dow: number; avg: number; views: number }>;
  /** hour 0..23 (local); avg active users + avg views (opens) in that hour per day. */
  hourly: Array<{ hour: number; avg: number; views: number }>;
  totalActive: number;
}

/** All-time weekday averages (for the «весь час» toggle on the Activity page). */
export interface ActivityWeekdayResponse {
  weekday: Array<{ dow: number; avg: number; views: number }>;
}

/** Deep revenue drill-in behind the «Виручка брутто» / «Середній чек» tiles. */
export interface RevenueDetailDTO {
  grossCents: number;
  netCents: number;
  refundCents: number;
  refundCount: number;
  count: number;
  avgCents: number;
  medianCents: number;
  minCents: number;
  maxCents: number;
  daysInPeriod: number;
  avgPerDayCents: number;
  /** Adaptive revenue trend: hourly (single day) / daily / weekly / monthly. */
  granularity: 'hour' | 'day' | 'week' | 'month';
  series: Array<{ label: string; gross: number; count: number; year?: number; range?: string }>;
  histogram: Array<{ label: string; count: number }>;
  newRevenueCents: number;
  renewedRevenueCents: number;
  newCount: number;
  renewedCount: number;
  newAvgCents: number;
  renewedAvgCents: number;
}

/** A dashboard tile's drill-in: which metric's contacts to list. */
export type DashboardMetric = 'active' | 'new' | 'renewed' | 'ended';

/** One contact behind a dashboard tile (members drill-in). */
export interface DashboardMember {
  telegramId: string;
  fullName: string;
  username: string | null;
  photoUrl: string | null;
  email: string | null;
  phone: string | null;
  status: SubscriptionStatus;
  /** Context date: «active until» (active), end date (ended), payment date (new/renewed). */
  detailAt: string | null;
}

export interface DashboardMembersResponse {
  items: DashboardMember[];
  total: number;
  page: number;
  hasMore: boolean;
}

export interface AdminPaymentDTO {
  id: string;
  telegramId: string | null;
  userName: string | null;
  userUsername: string | null;
  product: { id: string; title: string } | null;
  rawProduct: string | null;
  amountCents: number;
  currency: string;
  status: PaymentStatus;
  occurredAt: string;
  payerEmail: string | null;
  payerPhone: string | null;
  payerName: string | null;
  paymentMethod: string | null;
  externalRef: string | null;
  /** Воронка, з якої прийшла оплата. */
  source: string | null;
  /** Креатив, з якого прийшла людина — окрема вісь від `source`. */
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmContent: string | null;
  utmTerm: string | null;
  importedFrom: string;
}

/** Виміри мульти-селект-фільтра оплат/контактів. */
export const UTM_DIMENSIONS = ['utmSource', 'utmMedium', 'utmCampaign', 'utmContent', 'utmTerm'] as const;
export type UtmDimension = (typeof UTM_DIMENSIONS)[number];

/** Підписи вимірів у UI. */
export const UTM_DIMENSION_LABEL: Record<UtmDimension, string> = {
  utmSource: 'UTM source',
  utmMedium: 'UTM medium',
  utmCampaign: 'UTM campaign',
  utmContent: 'UTM content',
  utmTerm: 'UTM term',
};

/** Обрані значення по кожному виміру; `__none__` = «без мітки». */
export type UtmSelection = Partial<Record<UtmDimension, string[]>>;

/** Рядок звіту GET /admin/dashboard/utm. */
export interface UtmReportRow {
  value: string | null;
  title: string;
  /** Нові контакти: first-touch зафіксовано у вікні з цією міткою. */
  contacts: number;
  /** Оплати, у яких мітка стоїть на самому платежі. */
  payCount: number;
  payNetCents: number;
  /** Оплати у вікні, згруповані по first-touch мітці платника. */
  ftCount: number;
  ftNetCents: number;
}

export interface UtmReportDTO {
  dim: 'source' | 'medium' | 'campaign' | 'content' | 'term';
  items: UtmReportRow[];
  totals: Omit<UtmReportRow, 'value' | 'title'>;
}

export interface AdminPaymentsResponse {
  items: AdminPaymentDTO[];
  total: number;
  /** Net sum (succeeded − refunded) of ALL matching payments, not just the page. */
  sumCents: number;
  page: number;
  hasMore: boolean;
}

export interface AdminPaymentDetailDTO extends AdminPaymentDTO {
  rawStatus: string | null;
  provider: string | null;
  cardLast4: string | null;
  createdAt: string;
}

export interface AdminCategoryDetailDTO {
  id: string;
  icon: IconName;
  name: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  bunnyCollectionId: string | null;
  materials: Array<{
    id: string;
    title: string;
    type: MaterialType;
    isPublished: boolean;
  }>;
}

/** Реферальні налаштування — редагуються на сторінці «Налаштування».
 *  Дзеркало referralSettingsSchema на бекенді (services/settings.ts). */
export interface ReferralSettingsDTO {
  /** % комісії. Знімок пишеться в кожне нарахування, тож зміна діє лише
   *  на майбутні. */
  percent: number;
  /** Днів від переходу за посиланням на прив'язку реферера. */
  attributionWindowDays: number;
  /** Днів від прив'язки на кваліфіковану покупку. */
  purchaseWindowDays: number;
  /** Період повернення коштів — доки триває, бонус «в очікуванні». */
  holdDays: number;
  /** Текст умов на сторінці рефералки; порожній → типовий текст із чисел. */
  termsMarkdown: string;
}

export interface AdminSettingsDTO {
  referral: ReferralSettingsDTO;
  player: PlayerSettings;
}

/** Відповідь playback-ендпоінтів. `hlsUrl` і решта — лише для відео, коли
 *  сервер знає адресу потоку; інакше грає iframe Bunny з `iframeUrl`. */
export interface PlaybackDTO {
  iframeUrl: string;
  hlsUrl?: string;
  posterUrl?: string;
  startAt?: number;
  player?: PlayerSettings;
}

/** Особистий кабінет рефералки. Усі суми — гривні. */
export interface ReferralMeDTO {
  /** Код для посилання; повне посилання клієнт будує з поточного origin. */
  code: string;
  /** Готове посилання на Mini App — для нативного share у Telegram. */
  telegramLink: string;
  clicks: number;
  uniqueVisitors: number;
  /** Скільки людей закріпилось за цим реферером. */
  joined: number;
  percent: number;
  purchaseWindowDays: number;
  holdDays: number;
  termsMarkdown: string;
  totalEarned: number;
  available: number;
  pending: number;
  spent: number;
}

/** Одне поповнення в журналі: хто оплатив, скільки і скільки з того нараховано. */
export interface ReferralEarningDTO {
  id: string;
  /** Замасковане імʼя — «Марія К.». */
  name: string;
  /** Скільки людина реально сплатила грошима. */
  paid: number;
  percent: number;
  amount: number;
  status: 'pending' | 'available' | 'reversed';
  occurredAt: string;
  availableAt: string | null;
}

export interface ReferralEarningsResponse {
  items: ReferralEarningDTO[];
  nextCursor: string | null;
}

// ── Адмінське зведення рефералки. Імена тут НЕ маскуються. ──────────────────

export interface AdminReferrerDTO {
  telegramId: string;
  name: string;
  username: string | null;
  code: string | null;
  joined: number;
  totalEarned: number;
  available: number;
  pending: number;
  spent: number;
}

export interface AdminReferrersResponse {
  items: AdminReferrerDTO[];
  /** true — реферерів більше, ніж влізло у відповідь. */
  truncated: boolean;
}

/** Оплата, яку автоматика не нарахувала: продукт не розпізнався. */
export interface AdminPendingPaymentDTO {
  paymentId: string;
  telegramId: string;
  buyerName: string;
  referrerName: string;
  rawProduct: string | null;
  amount: number;
  occurredAt: string;
}

export type ReferralLedgerType = 'accrual' | 'reversal' | 'spend' | 'adjustment';

export interface AdminLedgerEntryDTO {
  id: string;
  type: ReferralLedgerType;
  amount: number;
  base: number | null;
  percent: number | null;
  referredName: string | null;
  externalRef: string | null;
  note: string | null;
  occurredAt: string;
  availableAt: string | null;
}

export interface AdminReferralDetailDTO {
  telegramId: string;
  name: string;
  username: string | null;
  code: string | null;
  referredBy: { telegramId: string; name: string; at: string | null; via: string | null } | null;
  available: number;
  pending: number;
  /** Останнє значення, віддане в SendPulse; розбіжність = дзеркало відстало. */
  syncedValue: number | null;
  syncedAt: string | null;
  entries: AdminLedgerEntryDTO[];
}

export interface MaterialChapter {
  title: string;
  start: number;
  end?: number;
}

export interface MaterialDetailDTO {
  id: string;
  type: MaterialType;
  title: string;
  description: string | null;
  durationSec: number | null;
  textContent: string | null; // legacy — read body instead
  thumbnailUrl: string | null;
  tags: string[];
  chapters: MaterialChapter[];
  body: Block[];
  publishedAt: string | null;
  isFavorite: boolean;
  category: { id: string; name: string; icon: IconName };
  /** When false, body/textContent are stripped server-side; render lock UI. */
  hasAccess: boolean;
}

// ── Коментарі ────────────────────────────────────────────────────────────────
// Дзеркало DTO з apps/api/src/lib/comments.ts і routes/admin-comments.ts.
// Спільного пакета між web і api немає — типи дублюються вручну, як і решта.

/** Стеля довжини коментаря. Мусить збігатися з COMMENT_MAX_LENGTH на сервері:
 *  лічильник у формі й zod-валідація рахують однаково (UTF-16 code units), тож
 *  «залишилось 12 символів» ніколи не перетворюється на 400 від сервера. */
export const COMMENT_MAX_LENGTH = 3000;

/**
 * Причини скарги. Коди — дзеркало COMMENT_REPORT_REASONS у
 * apps/api/src/lib/comments.ts; підписи живуть лише тут, тож переформулювати їх
 * можна без міграції і без правки вже збережених скарг.
 *
 * Порядок навмисний: спершу те, на що скаржаться найчастіше в клубі (спам і
 * образи), «Мені просто це не подобається» — передостаннім, бо це найслабший
 * сигнал і не має бути першим, за що чіпляється око.
 */
export const COMMENT_REPORT_REASONS = [
  'spam',
  'harassment',
  'scam',
  'misinformation',
  'nudity',
  'violence',
  'dislike',
  'other',
] as const;

export type CommentReportReason = (typeof COMMENT_REPORT_REASONS)[number];

export const COMMENT_REPORT_LABELS: Record<CommentReportReason, string> = {
  spam: 'Спам або реклама',
  harassment: 'Цькування або образи',
  scam: 'Шахрайство чи обман',
  misinformation: 'Неправдива інформація',
  nudity: 'Відвертий вміст',
  violence: 'Насильство або ворожнеча',
  dislike: 'Мені просто це не подобається',
  other: 'Інше',
};

/** Людський підпис причини. Невідомий код (стара скарга, зміна переліку)
 *  показуємо як є — краще сирий рядок, ніж порожньо. */
export function commentReportLabel(reason: string | null): string {
  if (!reason) return 'Без причини';
  return COMMENT_REPORT_LABELS[reason as CommentReportReason] ?? reason;
}

export interface CommentAuthorDTO {
  id: string;
  name: string;
  photoUrl: string | null;
  /** Відповідь команди — біля імені значок «Команда». */
  isAdmin: boolean;
}

export interface CommentDTO {
  id: string;
  parentId: string | null;
  author: CommentAuthorDTO;
  text: string;
  createdAt: string;
  likeCount: number;
  likedByMe: boolean;
  reportedByMe: boolean;
  /** Рахує сервер (автор або адмін) — клієнт лише малює дозволене. */
  canDelete: boolean;
  /** Лайкати може будь-хто залогінений, зокрема й власний коментар. */
  canLike: boolean;
  canReport: boolean;
  replies: CommentDTO[];
}

export interface CommentListResponse {
  items: CommentDTO[];
  /** Уся розмова — корені разом із відповідями (для заголовка «Коментарі (N)»). */
  total: number;
  page: number;
  hasMore: boolean;
}

export interface AdminCommentReportDTO {
  userId: string;
  fullName: string;
  reason: string | null;
  createdAt: string;
}

export interface AdminCommentDTO {
  id: string;
  parentId: string | null;
  text: string;
  createdAt: string;
  author: { id: string; fullName: string; username: string | null; photoUrl: string | null; isAdmin: boolean };
  /** На що відповідали — контекст для плаского списку адмінки. */
  parent: { id: string; text: string; authorName: string } | null;
  material: { id: string; title: string } | null;
  likeCount: number;
  reportCount: number;
  reviewedAt: string | null;
  deletedAt: string | null;
  deletedBy: string | null;
  reports: AdminCommentReportDTO[];
}

export interface AdminCommentsResponse {
  items: AdminCommentDTO[];
  total: number;
  page: number;
  hasMore: boolean;
}

export interface AdminCommentStatsDTO {
  total: number;
  /** Коментарі зі скаргами, які адмін ще не розглянув, — саме це число світить
   *  бейджем у розділі «Контент». */
  reportedPending: number;
}

/** Що саме показує адмінський список. */
export type AdminCommentStatus = 'all' | 'reported' | 'deleted';
